import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Search, UtensilsCrossed, ChevronDown, ChevronUp, Camera, Loader2, X, Download, Upload, Plus } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import ImportExcelModal from '../../components/shared/ImportExcelModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOS = ['Todos', 'Desayuno', 'Almuerzo', 'Cena'];
const TIPOS_FORM = ['Desayuno', 'Almuerzo', 'Cena'];
const ETIQUETAS = ['Todas', 'Bajar de Peso', 'Diabetes', 'Gastritis', 'Diabetes, Subir de Peso', 'Gastritis, Subir de Peso'];
const ETIQUETAS_FORM = ['Bajar de Peso', 'Diabetes', 'Gastritis', 'Subir de Peso', 'Diabetes, Subir de Peso', 'Gastritis, Subir de Peso'];
const COSTOS = ['Todos', 'Costo Bajo', 'Costo Medio', 'Costo Alto'];
const COSTOS_FORM = ['Costo Bajo', 'Costo Medio', 'Costo Alto'];

const COSTO_COLORS = {
  'Costo Bajo': 'bg-emerald-100 text-emerald-700',
  'Costo Medio': 'bg-amber-100 text-amber-700',
  'Costo Alto': 'bg-red-100 text-red-700',
};

const TIPO_COLORS = {
  'Desayuno': 'bg-amber-50 text-amber-700 border-amber-200',
  'Almuerzo': 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Cena': 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const ImageUploader = ({ itemId, currentUrl, collection, onUpdated }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/diet-image/${collection}/${itemId}`, formData);
      onUpdated(res.data.image_url);
    } catch (error) { console.error('Upload failed:', error); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="relative group">
      <div className="w-16 h-16 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 flex-shrink-0">
        {currentUrl ? (
          <img src={currentUrl.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${currentUrl}` : currentUrl}
            alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <UtensilsCrossed size={20} />
          </div>
        )}
      </div>
      <button onClick={() => fileRef.current?.click()}
        className="absolute inset-0 bg-black/40 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        data-testid={`upload-img-${itemId}`}>
        {uploading ? <Loader2 size={16} className="text-white animate-spin" /> : <Camera size={16} className="text-white" />}
      </button>
      <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
    </div>
  );
};

const EMPTY_PLATO = { nombre: '', descripcion: '', tipo_comida: 'Desayuno', ingredientes: '', etiquetas: '', receta: '', costo_promedio: 'Costo Medio' };

const CreatePlatoModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ ...EMPTY_PLATO });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API}/admin/platos`, form);
      toast.success('Plato creado exitosamente');
      onCreated(res.data);
      onClose();
    } catch (error) { toast.error('Error al crear plato'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="create-plato-modal">
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Nuevo Plato</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"><X size={18} className="text-gray-500" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Nombre *</label>
            <Input value={form.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Nombre del plato" className="mt-1" data-testid="plato-nombre" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Descripcion</label>
            <textarea value={form.descripcion} onChange={e => set('descripcion', e.target.value)} placeholder="Descripcion del plato"
              className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm resize-none h-20 focus:border-[#467322] focus:ring-1 focus:ring-[#467322] outline-none" data-testid="plato-descripcion" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tipo Comida</label>
              <select value={form.tipo_comida} onChange={e => set('tipo_comida', e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-[#467322] outline-none" data-testid="plato-tipo">
                {TIPOS_FORM.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Costo Promedio</label>
              <select value={form.costo_promedio} onChange={e => set('costo_promedio', e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-[#467322] outline-none" data-testid="plato-costo">
                {COSTOS_FORM.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Ingredientes</label>
            <Input value={form.ingredientes} onChange={e => set('ingredientes', e.target.value)} placeholder="Ej: Pollo, Arroz, Brocoli" className="mt-1" data-testid="plato-ingredientes" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Etiquetas</label>
            <select value={form.etiquetas} onChange={e => set('etiquetas', e.target.value)}
              className="mt-1 w-full h-10 px-3 rounded-xl border border-gray-200 text-sm focus:border-[#467322] outline-none" data-testid="plato-etiquetas">
              <option value="">Sin etiqueta</option>
              {ETIQUETAS_FORM.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Receta</label>
            <textarea value={form.receta} onChange={e => set('receta', e.target.value)} placeholder="Paso 1: ... Paso 2: ..."
              className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm resize-none h-24 focus:border-[#467322] focus:ring-1 focus:ring-[#467322] outline-none" data-testid="plato-receta" />
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50">Cancelar</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-[#467322] text-white font-bold text-sm hover:bg-[#3A601C] transition-colors disabled:opacity-50"
            data-testid="plato-save-btn">
            {saving ? <Loader2 size={14} className="inline animate-spin mr-1" /> : null} Guardar
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminPlatosPage = () => {
  const [platos, setPlatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('Todos');
  const [filterEtiqueta, setFilterEtiqueta] = useState('Todas');
  const [filterCosto, setFilterCosto] = useState('Todos');
  const [expandedId, setExpandedId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  const fetchPlatos = useCallback(() => {
    axios.get(`${API}/admin/platos`).then(res => setPlatos(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchPlatos(); }, [fetchPlatos]);

  const updateImage = (id, url) => {
    setPlatos(prev => prev.map(p => p.id === id ? { ...p, image_url: url } : p));
  };

  const exportData = () => {
    const blob = new Blob([JSON.stringify(platos, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `platos_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = platos.filter(p => {
    const matchSearch = !search || p.nombre?.toLowerCase().includes(search.toLowerCase()) || p.ingredientes?.toLowerCase().includes(search.toLowerCase());
    const matchTipo = filterTipo === 'Todos' || p.tipo_comida === filterTipo;
    const matchEtiqueta = filterEtiqueta === 'Todas' || p.etiquetas?.includes(filterEtiqueta.split(',')[0]);
    const matchCosto = filterCosto === 'Todos' || p.costo_promedio === filterCosto;
    return matchSearch && matchTipo && matchEtiqueta && matchCosto;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;

  return (
    <div data-testid="admin-platos-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Platos</h1>
          <p className="text-gray-500 text-sm mt-1">{filtered.length} de {platos.length} platos</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-[#467322] hover:text-[#467322] transition-all shadow-sm"
            data-testid="import-platos-btn">
            <Upload size={15} />
            <span className="hidden sm:inline">Importar</span>
          </button>
          <button onClick={exportData}
            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-[#467322] hover:text-[#467322] transition-all shadow-sm"
            data-testid="export-platos-btn">
            <Download size={15} />
            <span className="hidden sm:inline">Exportar</span>
          </button>
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#467322] text-white text-sm font-bold hover:bg-[#3A601C] transition-colors shadow-sm shadow-green-900/20"
            data-testid="new-plato-btn">
            <Plus size={15} />
            <span className="hidden sm:inline">Nuevo</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-5">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input placeholder="Buscar por nombre o ingredientes..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 bg-gray-50 border-transparent focus:bg-white focus:border-[#467322] rounded-xl h-10" data-testid="search-platos" />
          </div>
          <select value={filterTipo} onChange={e => setFilterTipo(e.target.value)}
            className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:border-[#467322] outline-none" data-testid="filter-tipo">
            {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={filterEtiqueta} onChange={e => setFilterEtiqueta(e.target.value)}
            className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:border-[#467322] outline-none" data-testid="filter-etiqueta">
            {ETIQUETAS.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          <select value={filterCosto} onChange={e => setFilterCosto(e.target.value)}
            className="h-10 px-3 rounded-xl border border-gray-200 text-sm bg-gray-50 focus:border-[#467322] outline-none" data-testid="filter-costo">
            {COSTOS.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(plato => {
          const isExpanded = expandedId === plato.id;
          return (
            <div key={plato.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`plato-${plato.id}`}>
              <div className="flex items-center gap-4 p-4">
                <ImageUploader itemId={plato.id} currentUrl={plato.image_url} collection="platos" onUpdated={url => updateImage(plato.id, url)} />
                <button onClick={() => setExpandedId(isExpanded ? null : plato.id)}
                  className="flex-1 flex items-center gap-4 text-left hover:bg-gray-50/50 transition-colors rounded-lg -m-1 p-1">
                  <div className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border flex-shrink-0 ${TIPO_COLORS[plato.tipo_comida] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                    {plato.tipo_comida}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{plato.nombre}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{plato.ingredientes}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {plato.etiquetas && <span className="text-[10px] font-semibold bg-violet-100 text-violet-700 px-2 py-0.5 rounded-md hidden sm:inline">{plato.etiquetas}</span>}
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${COSTO_COLORS[plato.costo_promedio] || 'bg-gray-100 text-gray-600'}`}>{plato.costo_promedio}</span>
                    {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </div>
                </button>
              </div>
              {isExpanded && (
                <div className="border-t border-gray-100 p-5 animate-fade-in space-y-4">
                  {plato.descripcion && (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">Descripcion</p>
                      <p className="text-sm text-gray-700 leading-relaxed">{plato.descripcion}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">Ingredientes</p>
                      <div className="flex flex-wrap gap-1.5">
                        {plato.ingredientes?.split(',').map((ing) => (
                          <span key={ing.trim()} className="text-xs bg-emerald-50 text-emerald-700 px-2 py-1 rounded-lg border border-emerald-100">{ing.trim()}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">Info</p>
                      <div className="flex gap-2 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-lg border ${TIPO_COLORS[plato.tipo_comida] || ''}`}>{plato.tipo_comida}</span>
                        {plato.etiquetas && <span className="text-xs bg-violet-50 text-violet-700 px-2 py-1 rounded-lg border border-violet-100">{plato.etiquetas}</span>}
                        <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${COSTO_COLORS[plato.costo_promedio] || ''}`}>{plato.costo_promedio}</span>
                      </div>
                    </div>
                  </div>
                  {plato.receta && (
                    <div>
                      <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">Receta</p>
                      <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700 leading-relaxed">
                        {plato.receta.split(/Paso \d+:/).filter(Boolean).map((paso, i) => (
                          <div key={`paso-${i}`} className="flex gap-2 mb-2 last:mb-0">
                            <span className="w-6 h-6 rounded-full bg-[#467322] text-white text-xs flex items-center justify-center flex-shrink-0 font-bold">{i + 1}</span>
                            <p className="mt-0.5">{paso.trim()}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <UtensilsCrossed size={40} className="mx-auto mb-3 opacity-30" />
            <p className="font-semibold">No se encontraron platos</p>
          </div>
        )}
      </div>

      {showImport && <ImportExcelModal collection="platos" onClose={() => setShowImport(false)} onImported={fetchPlatos} />}
      {showCreate && <CreatePlatoModal onClose={() => setShowCreate(false)} onCreated={(p) => setPlatos(prev => [p, ...prev])} />}
    </div>
  );
};

export default AdminPlatosPage;
